import RightPanelCard from "components/Common/RightPanelCard";
import CommonTable from "components/ReactTable/Table";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Container,
  Badge,
  FormGroup,
  Label,
  Input,
  CardFooter,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  UncontrolledTooltip,
} from "reactstrap";
import Select, { components } from "react-select";
import {
  customStyle,
  fixedWidthCustomStyle,
  DEFAULT_RECORDS_PER_PAGE,
  MASTER_STATUS_OPTIONS,
} from "components/Common/constants";
import NotificationAlert from "react-notification-alert";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSparePartList, addSparePart } from "actions/sparePart";
import { getManufacturerList } from "actions/manufacturer";
import { sparePartsList } from "constants/spareParts";
import { REGEX } from "components/Common/constants";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";
import moment from "moment";
import { exportSparePartsCSV } from "actions/sparePart";

const initialFields = {
  itemNumber: {
    value: "",
    error: "",
  },
  manufacturer: {
    value: "",
    error: "",
  },
  price: {
    value: "",
    error: "",
  },
  quantity: {
    value: "1",
    error: "",
  },
  description: {
    value: "",
    error: "",
  },
  status: {
    value: "ACTIVE",
    error: "",
  },
};

function SpareParts() {
  const dispatch = useDispatch();
  const notificationAlertRef = useRef(null);

  const spareParts = useSelector((state) => state.sparePart.sparePartsList);
  const manufacturerList = useSelector(
    (state) => state.manufacturer.manufacturerList,
  );

  const [pageNum, setPageNum] = useState(1);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sparePartsFormData, setSparePartsFormData] = useState(initialFields);
  const [sparePartListState, setSparePartListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
    showLoaderOnSaveClick: false,
  });
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchManufacturer: null,
    searchStatus: null,
    filterParams: [],
  });
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const filterJSON = [
      {
        field: "Status",
        condition: "eq",
        filterValues: ["ACTIVE"],
      },
    ];
    getManufacturerList(dispatch, { filters: JSON.stringify(filterJSON) });
  }, [dispatch]);

  useEffect(() => {
    getSparePartList(
      dispatch,
      pageNum,
      filterState.filterParams?.filters,
      filterState.filterParams?.searchString,
    );
    return () => {
      dispatch({ type: sparePartsList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (spareParts?.code === 200) {
      setSparePartListState({
        isLoading: false,
        error: "",
        noRecords: spareParts?.data?.sparePart?.length === 0,
        showLoaderOnClick: false,
        showLoaderOnSaveClick: false,
      });
    } else if (spareParts?.code || spareParts?.message) {
      setSparePartListState({
        isLoading: false,
        error:
          spareParts?.message ||
          "Failed to load spare parts list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
        showLoaderOnSaveClick: false,
      });
    }
  }, [spareParts]);

  const formattedData =
    spareParts?.data?.sparePart?.map((item, index) => ({
      ...item,
      manufacturerName: item?.manufacturer?.name || "",
      srNo:
        (pageNum - 1) *
          (spareParts?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    })) || [];

  const columns = [
  {
    Header: "Sr. no.",
    accessor: "srNo",
    className: "col-srNo",
    centerAlign:true,
  },
  {
    Header: "Item Number",
    accessor: "itemNumber",
    className: "col-itmNo",
    Cell: ({ value }) => (
      <span>{value}</span>
    ),
  },
  {
    Header: "Manufacturer",
    accessor: "manufacturer",
    className: "col-manufacturer", // fixed — was duplicated "col-itmNo"
    Cell: ({ value, row }) => row.original.manufacturer?.name || "",
  },
  {
    Header: "Description",
    accessor: "description",
    className: "col-desc",
  },
  {
    Header: "Unit Price (SAR)",
    accessor: "price",
    className: "col-price",
    Cell: ({ value }) => <span className="unit-price-cell">{value}</span>,
  },
  {
    Header: "Quantity",
    accessor: "quantity",
    className: "col-quantity",
    Cell: ({ value }) => <span className="quantity-cell">{value}</span>,
  },
  {
    Header: "Status",
    accessor: "status",
    className: "col-status", // added
    centerAlign: true,
    Cell: ({ value }) => (
      <div className="d-flex justify-content-center align-items-center h-100">
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value}
        </Badge>
      </div>
    ),
  },
  {
    Header: "Action",
    accessor: "actions",
    className: "col-actions", // was just "text-center"
    disableSortBy: true,
    Cell: ({ row }) => (
      <div className="actions-right d-flex justify-content-start">
        <div>
          <Button
            className="btn-icon icon-info"
            color="link"
            id={`edit${urlEncodedId(row.original.ID)}`}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();

              const encodedId = urlEncodedId(row.original.ID);

              window.open(
                `/admin/spare-parts/${encodedId}/details?edit=true`,
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            <i className="tim-icons icon-pencil tim-md" />
          </Button>
          <UncontrolledTooltip
            delay={0}
            target={`edit${urlEncodedId(row.original.ID)}`}
          >
            Edit
          </UncontrolledTooltip>
        </div>
      </div>
    ),
  },
];

  const getValidationError = (name, value) => {
    const isEmpty = value === "";

    switch (name) {
      case "itemNumber":
        return isEmpty ? "Please enter a item number" : "";

      case "manufacturer":
        return !value ? "Please select a manufacturer" : "";

      case "price":
        if (isEmpty) return "Please enter a price";
        if (Number(value) <= 0) return "Enter number greater than 0";

        // it allow integers or decimals
        return REGEX.INT_DEC_REGEX.test(value) ? "" : "Enter valid price";

      case "quantity":
        if (isEmpty) return "Please enter a quantity";
        if (Number(value) <= 0) return "Enter number greater than 0";

        // it allow only whole numbers and no decimal
        return REGEX.WHOLE_NUM_REGEX.test(value) ? "" : "Enter valid quantity";

      case "description":
        return isEmpty ? "Please enter a description" : "";

      default:
        return "";
    }
  };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const validateForm = () => {
    let newUser = { ...sparePartsFormData };

    Object.keys(newUser).forEach((key) => {
      newUser[key] = {
        ...newUser[key],
        error: getValidationError(key, newUser[key].value),
      };
    });

    setSparePartsFormData(newUser); // set the error state

    return Object.values(newUser).every((item) => item.error === "");
  };

  const handleSearch = () => {
    const searchString = filterState.searchName.trim();

    const newParams = {
      filters: buildFilterParams(),
      searchString,
    };

    // Nothing changed AND we're not clearing an active search -> no-op
    if (
      JSON.stringify(filterState.filterParams) === JSON.stringify(newParams)
    ) {
      return;
    }

    setSparePartListState((prev) => ({
      ...prev,
      showLoaderOnClick: true,
      isLoading: true,
    }));
    setFilterState((prev) => ({ ...prev, filterParams: newParams }));

    if (pageNum !== 1) setPageNum(1);
  };

  const buildFilterParams = () => {
    const filterParams = [];
    if (filterState.searchManufacturer) {
      filterParams.push({
        field: "ManufacturerID",
        condition: "eq",
        filterValues: [String(filterState.searchManufacturer.ID)],
      });
    }
    if (filterState.searchStatus) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleClear = () => {
    setSparePartListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setFilterState({
      searchName: "",
      searchManufacturer: "",
      searchStatus: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleOnBlur = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmed);
    setSparePartsFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmed,
        error,
      },
    }));
  };

  const handleChargesChange = (e) => {
    const raw = e.target.value;
    if (raw === "" || REGEX.ONLY_NUMBERS_REGEX.test(raw)) {
      const normalized = raw.startsWith(".") ? `0${raw}` : raw;
      setSparePartsFormData((prev) => ({
        ...prev,
        price: { ...prev.price, value: normalized, error: "" },
      }));
    }
  };

  // Add submit -> call add Service API
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      //showloader
      setSparePartListState((prev) => ({
        ...prev,
        showLoaderOnSaveClick: true,
      }));
      const bodyData = {
        manufacturerID: sparePartsFormData.manufacturer.value.ID,
        regionID: 1, // define constant.
        itemNumber: sparePartsFormData.itemNumber.value,
        price: parseFloat(sparePartsFormData.price.value),
        quantity: parseInt(sparePartsFormData.quantity.value),
        description: sparePartsFormData.description.value,
        status: sparePartsFormData.status.value,
      };

      const res = await addSparePart(bodyData);

      if (res?.code === 200) {
        notify("success", res.message || "Spare Part added successfully");

        // reload list
        if (pageNum === 1)
          getSparePartList(
            dispatch,
            pageNum,
            filterState.filterParams,
            filterState.filterParams?.searchString,
          );
        else setPageNum(1);

        closeAddModal();
      } else {
        notify("danger", res?.message || "Failed to add spare part!");
      }
    } catch (err) {
      notify("danger", "Failed to add spare part!");
    } finally {
      setSparePartListState((prev) => ({
        ...prev,
        showLoaderOnSaveClick: false,
      }));
    }
  };

  const hanldeOnRowClick = (rowData) => {
  const encodedId = urlEncodedId(rowData.ID);

  window.open(
    `/admin/spare-parts/${encodedId}/details`,
    "_blank",
    "noopener,noreferrer",
  );
};

  const openAddModal = () => {
    setIsModalOpen(!isModalOpen);
  };
  const closeAddModal = () => {
    setIsModalOpen(false);
    if (isModalOpen) {
      setSparePartsFormData(initialFields);
    }
  };

  const handleChange = (fieldName, newValue) => {
    setSparePartsFormData((prev) => ({
      ...prev,
      [fieldName]: {
        value: newValue,
        error: "",
      },
    }));
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const handleExportCsv = async () => {
    try {
      setIsDownloading(true);
      const res = await exportSparePartsCSV({
        filters: filterState.filterParams?.filters,
        searchString: filterState.filterParams?.searchString,
      });
      if (res.status === 200) {
        const blob = new Blob(["\uFEFF" + res?.data], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `spare-parts-report_${moment().format("YYYY-MM-DD")}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        notify("success", res?.message || "CSV exported successfully");
      } else {
        const message = Array.isArray(res?.message || res?.data?.message)
          ? res?.message[0]?.Msg || res?.data?.message[0]?.Msg
          : res?.message || res?.data?.message || "Failed to export csv, try again later";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Something went wrong");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {isDownloading && (
        <div className="loading-overlay">
          <div className="loading-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Processing… Please wait.
            </div>
          </div>
        </div>
      )}
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container fluid className="mt-4">
        <Row>
          <Col className="mb-5" md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between mb-2">
                <CardTitle tag="h3" className="font-weight-normal">
                  Spare Parts
                </CardTitle>
                <div className="d-flex align-items-center">
                  <Button
                    className="btn-simple btn-round btn-style mr-3"
                    color="default"
                    size="md"
                    onClick={handleExportCsv}
                  >
                    <i className="fas fa-arrow-up-from-bracket mr-1" /> Export
                    Spare Parts
                  </Button>
                  <Button
                    className="btn-round btn-style"
                    color="default"
                    size="md"
                    outline
                    onClick={openAddModal}
                  >
                    <i className="fa fa-plus mr-1" />
                    Add Spare Part
                  </Button>
                </div>
              </CardHeader>
              <CardBody className="pt-2">
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by item no. , description, price or quantity"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />
                    <Select
                      className="mr-2 mb-2 filter-select"
                      classNamePrefix="react-select"
                      options={manufacturerList?.data?.manufacturer}
                      placeholder="Manufacturer"
                      value={filterState.searchManufacturer}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.ID}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchManufacturer: selected,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={fixedWidthCustomStyle()}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={MASTER_STATUS_OPTIONS}
                      placeholder="Status"
                      value={filterState.searchStatus}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleSearch}
                    >
                      {sparePartListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>
                    {Object.keys(filterState.filterParams).length > 0 && (
                      <Button
                        color="default"
                        className="mb-3 filter-btn btn-simple mt-2"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 mb-3 mx-1">
                  {spareParts?.data?.totalRecords ? (
                    <span className="font-weight-bold">
                      Total Spare Parts: {spareParts?.data?.totalRecords}
                    </span>
                  ) : null}
                </div>

                {sparePartListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : sparePartListState.error ? (
                  <>
                    <div className="loaderContainer">
                      {sparePartListState.error}
                    </div>
                  </>
                ) : sparePartListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No Spera part found.
                  </div>
                ) : (
                  <div className="table-responsive spare-parts-table">
                    <CommonTable
                      showPaginationBottom={true}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      data={formattedData}
                      resizable={false}
                      columns={columns}
                      className="-striped -highlight table-hover"
                      rowClick={hanldeOnRowClick}
                      totalPages={Math.ceil(
                        (spareParts?.data?.totalRecords || 0) /
                          (spareParts?.data?.noOfRecordsPerPage ||
                            DEFAULT_RECORDS_PER_PAGE),
                      )}
                      setPageNum={setPageNum}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <RightPanelCard isOpen={isModalOpen} onClose={closeAddModal}>
          <Card className="mb-0">
            <CardHeader
              className="d-flex align-items-center justify-content-between mb-2 flex-shrink-0"
              tag="h4"
            >
              Add Spare Part
              <i
                className="fa fa-close text-muted pointer"
                onClick={closeAddModal}
              />
            </CardHeader>

            <CardBody>
              <Row>
                <Col md="6" className="mb-3">
                  <FormGroup
                    className={`has-label ${
                      sparePartsFormData.itemNumber.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                  >
                    <Label>
                      Item Number <span className="validation-error">*</span>
                    </Label>
                    <Input
                      name="itemNumber"
                      type="text"
                      placeholder="Enter item number"
                      autoComplete="off"
                      maxLength={100}
                      value={sparePartsFormData.itemNumber.value}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      onBlur={(e) =>
                        handleOnBlur(e.target.name, e.target.value)
                      }
                    />
                    {sparePartsFormData.itemNumber.error && (
                      <small className="validation-error">
                        {sparePartsFormData.itemNumber.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-3">
                  <FormGroup
                    className={`has-label ${
                      sparePartsFormData.manufacturer.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                  >
                    <Label>
                      Manufacturer <span className="validation-error">*</span>
                    </Label>

                    <Select
                      className={`text-left ${
                        sparePartsFormData.manufacturer.error
                          ? "is-invalid"
                          : ""
                      }`}
                      classNamePrefix="react-select"
                      options={manufacturerList?.data?.manufacturer}
                      placeholder="Select Manufacturer"
                      styles={customStyle}
                      value={sparePartsFormData.manufacturer.value}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.ID}
                      onChange={(selected) =>
                        handleChange("manufacturer", selected)
                      }
                      onBlur={() =>
                        handleOnBlur(
                          "manufacturer",
                          sparePartsFormData.manufacturer.value,
                        )
                      }
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                    {sparePartsFormData.manufacturer.error && (
                      <small className="validation-error">
                        {sparePartsFormData.manufacturer.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-3">
                  <FormGroup
                    className={`has-label ${
                      sparePartsFormData.price.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                  >
                    <Label>
                      Price <span className="validation-error">*</span>
                    </Label>
                    <InputGroup className="input-group">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText className="custom-border d-flex align-items-center text-muted">
                          SAR
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        name="price"
                        type="text"
                        maxLength={7}
                        placeholder="Enter price"
                        autoComplete="off"
                        value={sparePartsFormData.price.value}
                        onChange={handleChargesChange}
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                      />
                    </InputGroup>
                    {sparePartsFormData.price.error && (
                      <small className="validation-error">
                        {sparePartsFormData.price.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-3">
                  <FormGroup
                    className={`has-label ${
                      sparePartsFormData.quantity.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                  >
                    <Label for="quantity">
                      Quantity <span className="validation-error">*</span>
                    </Label>
                    <Input
                      name="quantity"
                      type="text"
                      min={1}
                      maxLength={9}
                      placeholder="Enter quantity"
                      autoComplete="off"
                      value={sparePartsFormData.quantity.value}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      onBlur={(e) =>
                        handleOnBlur(e.target.name, e.target.value)
                      }
                    />
                    {sparePartsFormData.quantity.error && (
                      <small className="validation-error">
                        {sparePartsFormData.quantity.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="12" className="mb-3">
                  <FormGroup
                    className={`has-label ${
                      sparePartsFormData.description.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                  >
                    <Label for="quantity">
                      Description <span className="validation-error">*</span>
                    </Label>
                    <Input
                      name="description"
                      type="textarea"
                      cols="80"
                      rows="4"
                      maxLength={500}
                      placeholder="Enter description"
                      autoComplete="off"
                      value={sparePartsFormData.description.value}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      onBlur={(e) =>
                        handleOnBlur(e.target.name, e.target.value)
                      }
                    />
                    {sparePartsFormData.description.error && (
                      <small className="validation-error">
                        {sparePartsFormData.description.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6" className="mb-3">
                  <FormGroup className="status-group-product">
                    <Label for="status" className="mb-0">
                      Status <span className="validation-error ml-2">*</span>
                    </Label>

                    <div className="form-check-product">
                      <Input
                        type="radio"
                        name="status"
                        value="ACTIVE"
                        checked={sparePartsFormData.status.value === "ACTIVE"}
                        onChange={(e) =>
                          handleChange(e.target.name, e.target.value)
                        }
                      />
                      <Label for="statusActive" className="mb-0">
                        Active
                      </Label>
                    </div>

                    <div className="form-check-product">
                      <Input
                        type="radio"
                        name="status"
                        value="INACTIVE"
                        checked={sparePartsFormData.status.value === "INACTIVE"}
                        onChange={(e) =>
                          handleChange(e.target.name, e.target.value)
                        }
                      />
                      <Label for="statusInactive" className="mb-0">
                        Inactive
                      </Label>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <div className="d-flex justify-content-end mb-0">
                <Button
                  color="secondary"
                  onClick={closeAddModal}
                  className="mr-2"
                >
                  Cancel
                </Button>
                <Button
                  color="default"
                  type="submit"
                  onClick={handleAddSubmit}
                  disabled={Object.values(sparePartsFormData).some(
                    (item) => item.error !== "",
                  )}
                >
                  {sparePartListState.showLoaderOnSaveClick ? (
                    <i className="fa fa-spinner fa-spin" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </CardFooter>
          </Card>
        </RightPanelCard>
      </Container>
    </>
  );
}

export default SpareParts;
